import React, { useCallback, useRef, useState } from 'react';

interface MessageInputProps {
  onSubmit: (message: string) => void;
  textareaDisabled: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({ onSubmit, textareaDisabled }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    if (!textareaDisabled && !value.trim()) return;
    onSubmit(value.trim());
    setValue('');
  }, [value, textareaDisabled, onSubmit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Button disabled only when textarea is active AND there is no text typed yet
  const buttonDisabled = !textareaDisabled && !value.trim();

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-grey-91 px-4 py-3">
      <div className="flex items-center gap-2">
        <textarea
          ref={inputRef}
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={textareaDisabled}
          placeholder="Type a message..."
          className="flex-1 resize-none bg-grey-99 border border-grey-91 rounded-pill px-4 py-2 text-base text-navy placeholder:text-navy-64 focus:outline-none focus:border-navy/30 disabled:opacity-50 disabled:pointer-events-none leading-relaxed"
          style={{ maxHeight: '96px', overflowY: 'auto' }}
        />
        <button
          onClick={handleSubmit}
          disabled={buttonDisabled}
          aria-label="Send message"
          className="flex-shrink-0 w-9 h-9 bg-coral hover:bg-coral-dark disabled:opacity-40 rounded-pill flex items-center justify-center transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
